import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import AppShell from "./components/AppShell";
import RequireAuth from "./components/RequireAuth";

/**
 * Five surfaces, one bundle, split at the route.
 *
 * The split is not cosmetic: a patient opening the tracker on 3G outside the
 * clinic should download the tracker, not the console's dialogs, the doctor's
 * chamber view and the whole administration dashboard as well.
 *
 * Administration is one chunk rather than four, because an administrator who
 * opens the staff page opens the rota in the same sitting — splitting further
 * would trade a faster first paint for a stall on every tab.
 */
const LoginPage = lazy(() => import("./routes/LoginPage"));
const DashboardPage = lazy(() => import("./routes/DashboardPage"));
const SessionPicker = lazy(() => import("./routes/SessionPicker"));
const CalendarPage = lazy(() => import("./routes/CalendarPage"));
const ConsolePage = lazy(() => import("./routes/ConsolePage"));
const RosterPage = lazy(() => import("./routes/RosterPage"));
const PatientsPage = lazy(() => import("./routes/PatientsPage"));
const TrackerPage = lazy(() => import("./routes/TrackerPage"));
const BoardPage = lazy(() => import("./routes/BoardPage"));
const ClinicBoardPage = lazy(() => import("./routes/ClinicBoardPage"));
const ChamberPage = lazy(() => import("./routes/ChamberPage"));
const AdminPage = lazy(() => import("./routes/AdminPage"));
const OnboardPage = lazy(() => import("./routes/OnboardPage"));

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="centred muted">Loading…</div>}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* The tracker link is what goes out over WhatsApp, so the path has
              to match the one the confirmation message builds. */}
          <Route path="/t/:trackingUuid" element={<TrackerPage />} />
          {/* Everything a member of staff navigates between, inside one frame:
              header, navigation rail, and the signed-in account. Wrapped once
              here rather than per route, so a screen added later cannot forget
              to be reachable — and `RequireAuth` gates the frame rather than
              each page inside it. */}
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            {/* Two paths, one page. `/dashboard` is the real one and works on
                any deployment; `/` is what the app falls back to and what the
                edge proxy rewrites to the shell on the application hostname —
                on a single-host deployment the server answers `/` with the
                marketing page, so the dashboard could not live there alone. */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/" element={<DashboardPage />} />
            <Route path="/sessions" element={<SessionPicker />} />
            {/* Across dates rather than within one: "when can you fit me in?"
                is the question the session picker cannot answer. */}
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/console/:sessionId" element={<ConsolePage />} />
            {/* The other axis through the same tokens: the roster answers "who
                is booked on this day", this answers "when is this patient
                booked". */}
            <Route path="/patients" element={<PatientsPage />} />
            <Route path="/roster/:sessionId" element={<RosterPage />} />
            <Route path="/chamber" element={<ChamberPage />} />
            {/* The section is a route parameter, not component state, so a link
                to the rota is a link to the rota. */}
            <Route path="/admin/:section?" element={<AdminPage />} />
            {/* Superadmin only, and enforced by the server rather than by
                hiding the link: a route that is merely absent from a menu is
                not a permission. Inside the frame because it is a page somebody
                navigates *from* — into a clinic, and back out again. */}
            <Route path="/onboard" element={<OnboardPage />} />
          </Route>
          {/* The whole clinic on one screen, for a polyclinic waiting room.
              React Router ranks a static segment above a dynamic one, so this
              wins over `/board/:sessionId` regardless of declaration order. */}
          <Route
            path="/board"
            element={
              <RequireAuth>
                <ClinicBoardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/board/:sessionId"
            element={
              <RequireAuth>
                <BoardPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
